import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Mail01Icon } from '@hugeicons/core-free-icons';
import { authEnabled } from '../../lib/auth';
import { capture } from '../../lib/analytics';
import { useAuthCardSubmit } from './useAuthCardSubmit';
import { DEFAULT_AFTER_AUTH_URL, AUTH_DISABLED_MESSAGE } from './auth-card.utils';
import {
  CLERK_LOAD_TIMEOUT_MS,
  CLERK_LOAD_FAILURE_MESSAGE,
  CLERK_LOAD_RETRY_LABEL,
} from '../AuthLoadingScreen';
import { claimClerkAutoRetry, clerkWakeRetrySpent } from '../../lib/clerkLoadRetry';
import { useClerkWakeRetry } from '../../hooks/useClerkWakeRetry';
import { claimAuthLoadFailureReport } from '../../lib/authLoadFailureReport';
import { elapsedMsSince, monotonicNow } from '../../lib/elapsedMs';
import { Logo, LogoMark } from '../ui/Logo';
import { log } from '@/lib/logger';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

// 48px `h-12 rounded-xl` is DELIBERATE, not drift from the app's `.input` / `.input-lg` tiers:
// sign-in and sign-up are conversion surfaces and keep their taller field, as onboarding's
// `ui/Input` keeps 44px. Decided 2026-09-22 (user_decisions_log). Focus glow and the disabled
// token set are already shared with the app tiers (inputFocusGlowParity / inputSystemParity).
const inputClassName = 'flex h-12 w-full rounded-xl border border-gray-200 bg-white px-4 oq-text-body text-gray-900 transition focus:border-primary-500 focus:outline-none focus-glow disabled:cursor-not-allowed disabled:opacity-50';

type AuthCardProps = {
  afterAuthUrl?: string;
  initialEmail?: string;
  initialView?: 'main' | 'forgot-password';
};

export function AuthCard({ afterAuthUrl = DEFAULT_AFTER_AUTH_URL, initialEmail = '', initialView = 'main' }: AuthCardProps) {
  const emailFocusedRef = useRef(false);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // TOS is accepted implicitly by continuing (Superagent pattern)
  // Clerk's legalAccepted is set to true on sign-up in useAuthCardSubmit

  log.info('[AuthCard] Rendering');

  const {
    view,
    setView,
    formError,
    isSubmitting,
    isInteractionDisabled,
    isCardReady,
    verificationEmail,
    handleGoogleAuth,
    handleEmailPasswordSubmit,
    handleVerifyEmail,
    handleSecondFactor,
    handleForgotPassword,
    handleResetPassword,
  } = useAuthCardSubmit({ afterAuthUrl, initialView });

  // clerk-js load watchdog: the whole card disables until Clerk hydrates
  // (isCardReady), so when clerk-js fails to load (network / ad blocker /
  // failed_to_load_clerk_js_timeout, observed blocking real signups
  // 2026-07-19/23) the visitor sat on a silently dead form forever. After
  // CLERK_LOAD_TIMEOUT_MS of not-ready, say so and offer a Retry (mirrors
  // AuthLoadingScreen's failure card for protected routes).
  const [clerkLoadTimedOut, setClerkLoadTimedOut] = useState(false);
  // Set once the automatic reload is claimed and fired. Suppresses the failure
  // alert so we do not flash a dead end at a visitor we are already rescuing.
  const [autoRetrying, setAutoRetrying] = useState(false);
  const clerkWaitStartedAtRef = useRef(monotonicNow());
  useEffect(() => {
    if (!authEnabled || isCardReady) {
      setClerkLoadTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setClerkLoadTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isCardReady]);
  const showClerkLoadFailure = clerkLoadTimedOut && !isCardReady && authEnabled && !autoRetrying;

  // A Clerk outage on /sign-in was invisible in product analytics (incident
  // 2026-07-29: a rescan-email conversion died on clerk.overlayqa.com timing
  // out, seen only as raw $exception noise). PostHog loads independently of
  // clerk-js, so capture works while Clerk is down. One-shot per mount: the
  // failure state can flip back off if Clerk hydrates late, and re-entering
  // it must not double-count the outage.
  //
  // Since 2026-08-23 the first failure per tab also RELOADS automatically. The
  // manual Retry below was measurably not working (3 of 28 people pressed it
  // over 2026-08-05..08-23 while 27 of 31 sessions ended on this screen), and a
  // reload provably recovers a failed clerk-js load. Reloading is safe here
  // specifically because the card is disabled until Clerk hydrates, so there is
  // no typed input to discard. The claim is shared with AuthLoadingScreen, so a
  // tab gets ONE automatic reload across both surfaces.
  const loadFailureReportedRef = useRef(false);
  useEffect(() => {
    const timedOutNow = clerkLoadTimedOut && !isCardReady && authEnabled;
    if (!timedOutNow || loadFailureReportedRef.current) return;
    loadFailureReportedRef.current = true;

    // claimClerkAutoRetry resolves and guards sessionStorage itself; reading
    // window.sessionStorage here would throw SecurityError on a blocked store.
    const willAutoRetry = claimClerkAutoRetry();

    // Once per DOCUMENT rather than once per instance; see
    // lib/authLoadFailureReport. The claim wraps only the capture, so a second
    // instance still reaches the reload branch below.
    // posthog-js flushes on pagehide, so this survives the reload below.
    if (claimAuthLoadFailureReport()) {
      capture('auth_load_failed', {
        surface: 'sign_in',
        attempt: willAutoRetry ? 1 : clerkWakeRetrySpent() ? 3 : 2,
        auto_retried: willAutoRetry,
        waited_ms: elapsedMsSince(clerkWaitStartedAtRef.current),
      });
    }

    if (willAutoRetry) {
      setAutoRetrying(true);
      window.location.reload();
    }
  }, [clerkLoadTimedOut, isCardReady]);

  // Same wake retry as AuthLoadingScreen, on the same shared claim: while the
  // failure alert is showing, reload once more when the visitor comes back to
  // the tab or the network returns. Safe for the reason the reload above is:
  // the card is disabled until Clerk hydrates, so nothing typed can be lost.
  useClerkWakeRetry(showClerkLoadFailure, 'sign_in');

  // --- Verify email view (sign-up flow) ---
  if (view === 'verify-email') {
    return (
      <div className="auth-card space-y-6 text-center animate-fade-in">
        <Logo showText size="md" className="justify-center" />
        <div className="flex justify-center">
          <HugeiconsIcon icon={Mail01Icon} size={48} strokeWidth={2} color="currentColor" className="text-primary-500" />
        </div>
        <div className="space-y-1">
          <h1 className="hero-title">
            Check your email
          </h1>
          <p className="oq-text-body text-gray-600">
            We sent a verification code to {verificationEmail}
          </p>
        </div>

        <form
          className="space-y-6 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerifyEmail(verificationCode);
          }}
        >
          <label className="block space-y-2">
            <span className="oq-text-label text-gray-700">Verification code</span>
            <input
              className={inputClassName}
              disabled={isInteractionDisabled}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter 6-digit code"
              type="text"
              value={verificationCode}
              autoComplete="one-time-code"
            />
          </label>

          <button className="auth-card__submit" disabled={isInteractionDisabled} type="submit">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="text-cap-trim">Verifying...</span>
              </span>
            ) : (
              <span className="text-cap-trim">Verify email</span>
            )}
          </button>
        </form>

        {formError && (
          <div className="auth-card__form-error text-left" role="alert">{formError}</div>
        )}

        <button
          className="oq-text-body text-gray-600 hover:text-gray-700 hover:underline"
          onClick={() => setView('main')}
          type="button"
        >
          <span className="text-cap-trim block">Back</span>
        </button>
      </div>
    );
  }

  // --- Two-factor view (email-code MFA on sign-in) ---
  if (view === 'two-factor') {
    return (
      <div className="auth-card space-y-6 text-center animate-fade-in">
        <Logo showText size="md" className="justify-center" />
        <div className="flex justify-center">
          <HugeiconsIcon icon={Mail01Icon} size={48} strokeWidth={2} color="currentColor" className="text-primary-500" />
        </div>
        <div className="space-y-1">
          <h1 className="hero-title">
            Two-step verification
          </h1>
          <p className="oq-text-body text-gray-600">
            Enter the code we sent to {verificationEmail}
          </p>
        </div>

        <form
          className="space-y-6 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSecondFactor(twoFactorCode);
          }}
        >
          <label className="block space-y-2">
            <span className="oq-text-label text-gray-700">Verification code</span>
            <input
              className={inputClassName}
              disabled={isInteractionDisabled}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder="Enter 6-digit code"
              type="text"
              value={twoFactorCode}
              autoComplete="one-time-code"
            />
          </label>

          <button className="auth-card__submit" disabled={isInteractionDisabled} type="submit">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="text-cap-trim">Verifying...</span>
              </span>
            ) : (
              <span className="text-cap-trim">Verify code</span>
            )}
          </button>
        </form>

        {formError && (
          <div className="auth-card__form-error text-left" role="alert">{formError}</div>
        )}

        <button
          className="oq-text-body text-gray-600 hover:text-gray-700 hover:underline"
          onClick={() => setView('main')}
          type="button"
        >
          <span className="text-cap-trim block">Back</span>
        </button>
      </div>
    );
  }

  // --- Forgot password view ---
  if (view === 'forgot-password') {
    return (
      <div className="auth-card space-y-6 text-center animate-fade-in">
        <Logo showText size="md" className="justify-center" />
        <div className="space-y-1">
          <h1 className="hero-title">
            Reset your password
          </h1>
          <p className="oq-text-body text-gray-600">
            Enter your email to receive a reset code.
          </p>
        </div>

        <form
          className="space-y-6 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void handleForgotPassword(email);
          }}
        >
          <label className="block space-y-2">
            <span className="oq-text-label text-gray-700">Email</span>
            <input
              className={inputClassName}
              disabled={isInteractionDisabled}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              type="email"
              value={email}
            />
          </label>

          <button className="auth-card__submit" disabled={isInteractionDisabled} type="submit">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="text-cap-trim">Sending...</span>
              </span>
            ) : (
              <span className="text-cap-trim">Send reset code</span>
            )}
          </button>
        </form>

        {formError && (
          <div className="auth-card__form-error text-left" role="alert">{formError}</div>
        )}

        <button
          className="oq-text-body text-gray-600 hover:text-gray-700 hover:underline"
          onClick={() => setView('main')}
          type="button"
        >
          <span className="text-cap-trim block">Back to sign-in</span>
        </button>
      </div>
    );
  }

  // --- Reset password view ---
  if (view === 'reset-password') {
    return (
      <div className="auth-card space-y-6 text-center animate-fade-in">
        <Logo showText size="md" className="justify-center" />
        <div className="space-y-1">
          <h1 className="hero-title">
            Finish resetting your password
          </h1>
        </div>

        <form
          className="space-y-6 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void handleResetPassword(resetCode, newPassword);
          }}
        >
          <label className="block space-y-2">
            <span className="oq-text-label text-gray-700">Reset code</span>
            <input
              className={inputClassName}
              disabled={isInteractionDisabled}
              onChange={(e) => setResetCode(e.target.value)}
              placeholder="Enter reset code"
              type="text"
              value={resetCode}
              autoComplete="one-time-code"
            />
          </label>

          <label className="block space-y-2">
            <span className="oq-text-label text-gray-700">New password</span>
            <input
              className={inputClassName}
              disabled={isInteractionDisabled}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              type="password"
              value={newPassword}
              autoComplete="new-password"
            />
          </label>

          <button className="auth-card__submit" disabled={isInteractionDisabled} type="submit">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="text-cap-trim">Updating...</span>
              </span>
            ) : (
              <span className="text-cap-trim">Update password</span>
            )}
          </button>
        </form>

        {formError && (
          <div className="auth-card__form-error text-left" role="alert">{formError}</div>
        )}
      </div>
    );
  }

  // --- Main view (unified sign-in / sign-up) ---
  return (
    <div className="auth-card space-y-6 text-center animate-fade-in">
      {/* Logo */}
      <div className="flex justify-center">
        <LogoMark className="w-10 h-10" />
      </div>

      {/* Heading */}
      <div className="space-y-1">
        <h1 className="hero-title">
          Sign in or create an account
        </h1>
        <p className="oq-text-body text-gray-600">
          Enter your email and password to continue.
        </p>
      </div>

      {/* Google button */}
      <button
        className="auth-card__google-button"
        disabled={isInteractionDisabled}
        onClick={() => void handleGoogleAuth()}
        type="button"
      >
        <GoogleIcon />
        <span className="text-cap-trim">Continue with Google</span>
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 oq-text-caption text-gray-600" style={{ backgroundColor: 'var(--color-surface)' }}>
            Or continue with email
          </span>
        </div>
      </div>

      {/* Email + Password form */}
      <form
        className="space-y-6 text-left"
        onSubmit={(e) => {
          e.preventDefault();
          void handleEmailPasswordSubmit(email, password);
        }}
      >
        <label className="block space-y-2">
          <span className="oq-text-label text-gray-700">Email</span>
          <input
            className={inputClassName}
            disabled={isInteractionDisabled}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => {
              if (emailFocusedRef.current) return;
              emailFocusedRef.current = true;
              capture('auth_email_focused', { surface: 'dashboard' });
            }}
            placeholder="Email address"
            type="email"
            value={email}
          />
        </label>

        <label className="block space-y-2">
          <span className="oq-text-label text-gray-700">Password</span>
          <input
            className={inputClassName}
            disabled={isInteractionDisabled}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            type="password"
            value={password}
            autoComplete="current-password"
          />
        </label>

        {/* Smart CAPTCHA mount: Clerk's custom sign-up flow requires a
            #clerk-captcha element to exist by the time signUp.create() runs.
            Without it clerk-js logs "Cannot initialize Smart CAPTCHA widget"
            (the recurring $exception) and falls back to an invisible widget
            that blocks users falsely detected as bots with no challenge to
            pass. Empty until Clerk renders the widget into it. */}
        <div id="clerk-captcha" className="flex justify-center empty:hidden" />

        <button
          className="auth-card__submit"
          disabled={isInteractionDisabled}
          type="submit"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span className="text-cap-trim">Signing in...</span>
            </span>
          ) : (
            <span className="text-cap-trim">Continue</span>
          )}
        </button>

        <div className="text-center">
          <button
            className="oq-text-body text-gray-600 hover:text-gray-700 hover:underline"
            onClick={() => setView('forgot-password')}
            type="button"
          >
            <span className="text-cap-trim block">Forgot my password</span>
          </button>
        </div>
      </form>

      {/* Passive TOS agreement */}
      <p className="oq-text-caption text-gray-600">
        By continuing, you agree to OverlayQA&apos;s
        <br />
        <a className="font-medium text-gray-500 underline hover:text-gray-700" href="https://overlayqa.com/terms/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        {' '}and{' '}
        <a className="font-medium text-gray-500 underline hover:text-gray-700" href="https://overlayqa.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </p>

      {/* Errors */}
      {!authEnabled && (
        <div className="auth-card__notice text-left" role="status">
          {AUTH_DISABLED_MESSAGE}
        </div>
      )}

      {showClerkLoadFailure && (
        <div className="auth-card__form-error text-left" role="alert">
          <p>{CLERK_LOAD_FAILURE_MESSAGE}</p>
          <button
            type="button"
            className="mt-2 font-medium underline"
            onClick={() => {
              // posthog-js flushes on pagehide, so the event survives the reload.
              capture('auth_load_retry_clicked', { surface: 'sign_in' });
              window.location.reload();
            }}
          >
            <span className="text-cap-trim block">{CLERK_LOAD_RETRY_LABEL}</span>
          </button>
        </div>
      )}

      {formError && (
        <div className="auth-card__form-error text-left" role="alert">
          {formError}
        </div>
      )}

    </div>
  );
}
